import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { ReviewCard } from '@/components/shared/ReviewCard';
import { Star } from 'lucide-react';

interface UserReviewsTabProps {
  userId: string;
}

interface Review {
  id: number;
  rating: number;
  comment?: string;
  created_at: string;
  highlighted: boolean;
  service_id: number;
  services?: {
    name: string;
    location?: string;
    image_url?: string;
  };
}

const UserReviewsTab: React.FC<UserReviewsTabProps> = ({ userId }) => {
  const { data: reviews, isLoading } = useQuery({
    queryKey: ['user-reviews', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('reviews')
        .select(`
          id,
          rating,
          comment,
          created_at,
          highlighted,
          service_id,
          services (
            name,
            location,
            image_url
          )
        `)
        .eq('customer_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Review[];
    }
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-24 bg-muted animate-pulse rounded" />
        ))}
      </div>
    );
  }

  if (!reviews || reviews.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Star className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>No reviews found</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {reviews.map((review) => (
        <ReviewCard
          key={review.id}
          review={review}
          showServiceInfo={true}
        />
      ))}
    </div>
  );
};

export default UserReviewsTab;